import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Result, Spin } from 'antd';
import { QRCodeSVG } from 'qrcode.react';
import { ClockCircleOutlined, CopyOutlined, ExportOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { paymentApi } from '../../../api/payment.api.js';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner.jsx';
import { ErrorState } from '../../../components/common/ErrorState.jsx';
import { useOrderPolling } from '../hooks/useOrderPolling.js';
import { PaymentComingSoon } from '../components/PaymentComingSoon.jsx';
import { CHECKOUT_ENABLED } from '../../../constants/payment.js';
import { useAuth } from '../../../contexts/AuthContext.jsx';
import { toast } from '../../../utils/toast.js';
import '../styles/checkout.css';

/**
 * Ảnh QR do qr.sepay.vn vẽ: trong lúc ảnh đang tải thì hiện vòng chờ (không để ô trống / chữ thay ảnh),
 * tải lỗi thì cho tải lại. Thông tin chuyển khoản tay bên dưới vẫn dùng được trong lúc chờ.
 */
function QrImage({ src, alt }) {
  const [state, setState] = useState('loading'); // loading | loaded | error
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setState('loading');
  }, [src]);

  const url = attempt ? `${src}${src.includes('?') ? '&' : '?'}_r=${attempt}` : src;

  return (
    <div className="checkout-qr-frame">
      {state !== 'error' && (
        <img
          key={url}
          className="checkout-qr-img"
          src={url}
          alt={alt}
          style={state === 'loaded' ? undefined : { visibility: 'hidden' }}
          onLoad={() => setState('loaded')}
          onError={() => setState('error')}
        />
      )}
      {state === 'loading' && (
        <div className="checkout-qr-overlay">
          <Spin size="large" />
          <span>Đang tạo mã QR...</span>
        </div>
      )}
      {state === 'error' && (
        <div className="checkout-qr-overlay">
          <span>Chưa tải được mã QR.</span>
          <Button icon={<ReloadOutlined />} onClick={() => { setAttempt((n) => n + 1); setState('loading'); }}>
            Tải lại mã QR
          </Button>
          <span className="checkout-qr-hint">Hoặc chuyển khoản tay theo thông tin bên dưới.</span>
        </div>
      )}
    </div>
  );
}

function formatVnd(amount) {
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);
}

// Trang quay về sau khi thanh toán (VD /tools/trac-nghiem-thuc-chien khi mua từ màn hình khóa công cụ).
// Chỉ nhận đường dẫn nội bộ để không bị lợi dụng chuyển hướng sang trang ngoài.
function getSafeReturnTo(value) {
  return value && value.startsWith('/') && !value.startsWith('//') ? value : '/dashboard';
}

async function copyText(text, label) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`Đã sao chép ${label}`);
  } catch {
    toast.error('Không sao chép được, vui lòng chép tay');
  }
}

function CopyRow({ label, value, copyValue, strong }) {
  return (
    <div className="checkout-bank-row">
      <span className="checkout-bank-label">{label}</span>
      <span className={`checkout-bank-value${strong ? ' checkout-bank-value--strong' : ''}`}>{value}</span>
      <button
        type="button"
        className="checkout-copy-btn"
        onClick={() => copyText(String(copyValue ?? value), label.toLowerCase())}
        aria-label={`Sao chép ${label}`}
      >
        <CopyOutlined />
      </button>
    </div>
  );
}

function useCountdown(expiredAt) {
  const [remainingMs, setRemainingMs] = useState(() => (expiredAt ? new Date(expiredAt).getTime() - Date.now() : 0));

  useEffect(() => {
    if (!expiredAt) return undefined;
    const timer = setInterval(() => {
      setRemainingMs(new Date(expiredAt).getTime() - Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, [expiredAt]);

  const clamped = Math.max(0, remainingMs);
  const minutes = Math.floor(clamped / 60000);
  const seconds = Math.floor((clamped % 60000) / 1000);
  return { remainingMs: clamped, label: `${minutes}:${String(seconds).padStart(2, '0')}` };
}

export function CheckoutPage() {
  const navigate = useNavigate();
  // Thanh toán chưa mở: báo đang triển khai thay vì gọi API tạo đơn (tránh hiện lỗi cho học viên)
  if (!CHECKOUT_ENABLED) {
    return (
      <div className="main-container">
        <PaymentComingSoon
          extra={
            <Button key="back" onClick={() => navigate('/courses?tab=pricing')}>
              Xem lại bảng giá
            </Button>
          }
        />
      </div>
    );
  }
  return <CheckoutFlow />;
}

function CheckoutFlow() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = getSafeReturnTo(searchParams.get('returnTo'));
  const backToTool = returnTo.startsWith('/tools');
  const { refreshUser } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [redirected, setRedirected] = useState(false);

  const loadOrder = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await paymentApi.createOrder(planId);
      setOrder(res.data);
    } catch (err) {
      setError(err.message || 'Không thể khởi tạo thanh toán');
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const { status } = useOrderPolling(order);
  const { remainingMs, label } = useCountdown(order?.expiredAt);
  const isExpired = status === 'PENDING' && remainingMs <= 0;
  const isWaitingPayment = !!order && status === 'PENDING' && !isExpired;

  // Đang chờ thanh toán: trình duyệt hỏi lại nếu khách đóng / tải lại trang (hệ thống đang chờ SePay báo có tiền)
  useEffect(() => {
    if (!isWaitingPayment) return undefined;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isWaitingPayment]);

  useEffect(() => {
    if (status === 'PAID' && !redirected) {
      setRedirected(true);
      toast.success(backToTool ? 'Thanh toán thành công! Đang mở công cụ...' : 'Thanh toán thành công! Đang mở khóa nội dung...');
      // Tải lại hồ sơ để gói mới có hiệu lực ngay trên giao diện, rồi quay về trang khách đang dùng
      refreshUser();
      const timer = setTimeout(() => navigate(returnTo, { replace: true }), 1500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [status, redirected, navigate, refreshUser, returnTo, backToTool]);

  if (loading) {
    return <LoadingSpinner tip="Đang tạo mã QR thanh toán..." minHeight="60vh" />;
  }

  if (error) {
    return <ErrorState title="Không thể khởi tạo thanh toán" subTitle={error} onRetry={loadOrder} />;
  }

  if (!order) return null;

  if (status === 'PAID') {
    return (
      <div className="main-container">
        <Result
          status="success"
          title="Thanh toán thành công!"
          subTitle={`Gói ${order.plan?.name || ''} đã được kích hoạt cho tài khoản của bạn.`}
          extra={
            <Button type="primary" onClick={() => navigate(returnTo, { replace: true })}>
              {backToTool ? 'Mở công cụ ngay' : 'Vào học ngay'}
            </Button>
          }
        />
      </div>
    );
  }

  if (isExpired || status === 'EXPIRED' || status === 'CANCELLED' || status === 'FAILED') {
    return (
      <div className="main-container">
        <Result
          status="warning"
          title={status === 'FAILED' ? 'Thanh toán không thành công' : 'Mã QR đã hết hạn'}
          subTitle={
            <>
              Vui lòng tạo lại đơn hàng để tiếp tục thanh toán.
              <br />
              Nếu bạn <b>đã chuyển khoản</b> cho mã QR này, đừng chuyển lại: hệ thống sẽ tự đối soát và kích hoạt gói
              trong vài phút. Sau 30 phút chưa thấy gói, vui lòng gọi hotline/Zalo{' '}
              <a href="tel:0812539539">0812.539.539</a> kèm ảnh chụp giao dịch.
            </>
          }
          extra={
            <Button type="primary" icon={<ReloadOutlined />} onClick={loadOrder}>
              Tạo lại đơn hàng
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="main-container checkout-page">
      <div className="checkout-card">
        <h1 className="checkout-title">Thanh toán gói {order.plan?.name}</h1>
        <p className="checkout-amount">{formatVnd(order.amount)}</p>

        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ textAlign: 'left', marginBottom: 16 }}
          message={<b>Không đóng hoặc tải lại trang này trong lúc thanh toán</b>}
          description={
            <>
              Giữ nguyên trang cho tới khi hiện <b>&quot;Thanh toán thành công&quot;</b> (thường vài giây sau khi chuyển
              khoản). Lỡ đóng trang sau khi đã chuyển khoản thì không cần chuyển lại: hệ thống tự đối soát và kích hoạt
              gói trong vài phút; sau 30 phút chưa có, gọi hotline/Zalo <a href="tel:0812539539">0812.539.539</a>.
            </>
          }
        />

        <div className="checkout-qr-box">
          {order.qrImageUrl ? (
            <QrImage src={order.qrImageUrl} alt={`Mã QR chuyển khoản ${order.paymentCode}`} />
          ) : (
            <QRCodeSVG value={order.qrCode} size={220} />
          )}
        </div>

        <div className="checkout-countdown">
          <ClockCircleOutlined /> Mã QR còn hiệu lực: <strong>{label}</strong>
        </div>

        <p className="checkout-hint">
          Mở app ngân hàng, quét mã trên để thanh toán. Hệ thống tự kích hoạt gói ngay khi nhận được tiền, không cần
          tải lại trang.
        </p>

        {order.bankInfo && (
          <div className="checkout-bank">
            <div className="checkout-bank-title">Hoặc chuyển khoản tay</div>
            <CopyRow label="Ngân hàng" value={order.bankInfo.bankCode} />
            <CopyRow label="Số tài khoản" value={order.bankInfo.accountNumber} />
            {order.bankInfo.accountName && <CopyRow label="Chủ tài khoản" value={order.bankInfo.accountName} />}
            <CopyRow label="Số tiền" value={formatVnd(order.amount)} copyValue={order.amount} strong />
            <CopyRow label="Nội dung" value={order.paymentCode} strong />
            <p className="checkout-bank-note">
              Ghi <b>đúng nội dung {order.paymentCode}</b> để hệ thống tự nhận tiền. Sai hoặc thiếu nội dung thì gói không
              tự kích hoạt, vui lòng liên hệ hotline/Zalo <a href="tel:0812539539">0812.539.539</a>.
            </p>
          </div>
        )}

        {order.checkoutUrl && (
          <a href={order.checkoutUrl} target="_blank" rel="noopener noreferrer" className="checkout-fallback-link">
            <ExportOutlined /> Không quét được? Mở trang thanh toán PayOS
          </a>
        )}
      </div>
    </div>
  );
}
